'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

export default function HomePage() {
  const { user, isLoaded } = useUser();
  const router = useRouter();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    // If user is already logged in, redirect to document upload
    if (isLoaded && user) {
      router.push('/document-upload');
    }
  }, [user, isLoaded, router]);

  // Fetch user count
  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const response = await fetch('/api/stats/users');
        const data = await response.json();
        if (response.ok) {
          setUserCount(data.count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch user count:', error);
      }
    };
    
    fetchUserCount();
  }, []);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };
    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  // If user is confirmed logged in, show loading while redirecting.
  // Do NOT gate rendering on `!isLoaded` - the marketing content below must be
  // present in the initial server-rendered HTML for crawlers (including
  // non-JS-executing AI crawlers) to see it, rather than an empty spinner shell.
  if (isLoaded && user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="ArrowPathIcon" size={32} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[100dvh] flex items-center overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(59,130,246,0.1),transparent_50%)]"></div>
        </div>

        <div className="container mx-auto px-4 pt-16 pb-16 lg:pt-24 lg:pb-20 relative z-10">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-12 xl:gap-16 items-center">
          <div className="max-w-2xl text-center lg:text-left">
            {/* Logo */}
            <div className="flex items-center justify-center lg:justify-start mb-6 animate-fade-in">
              <div className="relative w-24 h-24 lg:w-28 lg:h-28">
                <Image
                  src="/logo.png"
                  alt="Offer XRay Logo"
                  width={128}
                  height={128}
                  priority
                  className="w-full h-full object-contain drop-shadow-xl"
                />
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6 leading-tight animate-fade-in-up">
              AI-Powered
              <span className="block mt-2 text-primary">
                Offer Letter XRay
              </span>
            </h1>

            <p className="text-lg lg:text-xl text-text-secondary mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up delay-100">
              Offer XRay is an AI-powered offer letter analyzer. Identify risks,
              understand compensation details, and negotiate with confidence.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up delay-200">
              <Link
                href="/signup"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-base hover:bg-primary/90 transition-all duration-300 shadow-xl hover:shadow-primary/40 hover:scale-105"
              >
                <Icon name="UserPlusIcon" size={20} />
                <span>Start Free Analysis</span>
                <Icon name="ArrowRightIcon" size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-surface text-text-primary border-2 border-border rounded-xl font-semibold text-base hover:bg-muted hover:border-primary/50 transition-all duration-300 shadow-md hover:scale-105"
              >
                <Icon name="ArrowRightOnRectangleIcon" size={20} />
                <span>Sign In</span>
              </Link>
            </div>
          </div>

          {/* Hero asset: real product screenshot, fills the space opposite the copy */}
          <div className="hidden lg:block animate-fade-in-up delay-200">
            <button
              type="button"
              onClick={() => setSelectedImage('/assets/images/Dashboard.png')}
              aria-label="Enlarge the Offer XRay analysis dashboard screenshot"
              className="group relative block w-full rounded-2xl overflow-hidden border border-border bg-surface shadow-2xl transition-transform duration-300 hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-muted/50">
                <span className="w-2.5 h-2.5 rounded-full bg-error/40" />
                <span className="w-2.5 h-2.5 rounded-full bg-warning/40" />
                <span className="w-2.5 h-2.5 rounded-full bg-success/40" />
                <span className="ml-2 text-xs font-medium text-text-secondary/80">Offer analysis</span>
              </div>
              <div className="relative aspect-[16/10] bg-muted/20">
                <Image
                  src="/assets/images/Dashboard.png"
                  alt="Offer XRay dashboard showing a risk score and a clause-by-clause breakdown of a job offer"
                  fill
                  priority
                  className="object-contain p-2"
                  sizes="(max-width: 1024px) 100vw, 640px"
                />
              </div>
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-12 lg:py-16 bg-surface/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-surface rounded-xl p-6 border border-border">
              <div className="text-3xl font-bold text-primary mb-2">
                {userCount !== null ? userCount.toLocaleString() : '-'}
              </div>
              <div className="text-text-secondary">People who have used Offer XRay</div>
            </div>
            <div className="bg-surface rounded-xl p-6 border border-border">
              <Icon name="CpuChipIcon" size={24} className="text-primary mb-3" />
              <div className="font-semibold text-text-primary mb-1">AI-driven analysis</div>
              <p className="text-text-secondary text-sm">Every offer letter is reviewed by AI trained on real employment contracts</p>
            </div>
            <div className="bg-surface rounded-xl p-6 border border-border">
              <Icon name="ClockIcon" size={24} className="text-primary mb-3" />
              <div className="font-semibold text-text-primary mb-1">Fast turnaround</div>
              <p className="text-text-secondary text-sm">Upload your offer and get a full breakdown in minutes</p>
            </div>
            <div className="bg-surface rounded-xl p-6 border border-border">
              <Icon name="LockClosedIcon" size={24} className="text-primary mb-3" />
              <div className="font-semibold text-text-primary mb-1">Privacy first</div>
              <p className="text-text-secondary text-sm">Documents are processed securely and never stored permanently</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-b from-background to-surface/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                How It Works
              </h2>
              <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                Get comprehensive insights into your job offer in 5 simple steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
              {/* Step 1 */}
              <div className="relative group">
                <div className="bg-surface rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl shadow-lg">
                    1
                  </div>
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon name="DocumentArrowUpIcon" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Document Upload</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Securely upload your offer letter in PDF, DOC, or DOCX format
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group">
                <div className="bg-surface rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl shadow-lg">
                    2
                  </div>
                  <div className="w-16 h-16 bg-success/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-success/20 transition-colors">
                    <Icon name="CpuChipIcon" size={32} className="text-success" />
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-4">AI Analysis</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Our AI analyzes compensation, benefits, leave policies, and legal terms
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group">
                <div className="bg-surface rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl shadow-lg">
                    3
                  </div>
                  <div className="w-16 h-16 bg-warning/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-warning/20 transition-colors">
                    <Icon name="ClipboardDocumentCheckIcon" size={32} className="text-warning" />
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Risk Assessment</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Get a comprehensive risk score with red, amber, and green indicators
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative group">
                <div className="bg-surface rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl shadow-lg">
                    4
                  </div>
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon name="LightBulbIcon" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Negotiation Guidance</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Receive personalized strategies, email templates, and talking points
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="relative group">
                <div className="bg-surface rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl shadow-lg">
                    5
                  </div>
                  <div className="w-16 h-16 bg-success/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-success/20 transition-colors">
                    <Icon name="DocumentTextIcon" size={32} className="text-success" />
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Export Report</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Generate a detailed PDF report to share or keep for reference
                  </p>
                </div>
              </div>
            </div>

            {/* Security Message */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-success/5 border border-success/20 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <Icon name="ShieldCheckIcon" size={24} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-lg font-semibold text-text-primary mb-1">Your Data is Secure</h4>
                    <p className="text-text-secondary">
                      Your offer letter is processed securely and never stored permanently. We only extract the insights you need.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Section - See It In Action */}
      <section className="py-20 lg:py-32 bg-background relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                See It In Action
              </h2>
              <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                Get a glimpse of the comprehensive analysis and insights you'll receive
              </p>
            </div>

            {/* Preview Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Dashboard Preview */}
              <div className="group relative">
                <div className="bg-gradient-to-br from-surface to-surface/80 rounded-2xl p-6 border-2 border-border hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:scale-[1.02]">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center shadow-lg border border-primary/20 group-hover:scale-110 transition-transform duration-300">
                      <Icon name="ChartBarIcon" size={28} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-text-primary mb-1">
                        AI-Powered Dashboard
                      </h3>
                      <p className="text-sm font-medium text-text-secondary/80">Comprehensive overview at a glance</p>
                    </div>
                  </div>
                  <div 
                    className="relative rounded-xl overflow-hidden border border-border shadow-lg group-hover:shadow-2xl transition-all duration-500 bg-muted/30 cursor-pointer"
                    onClick={() => setSelectedImage('/assets/images/Dashboard.png')}
                  >
                    <div className="aspect-video relative overflow-hidden flex items-center justify-center">
                      <Image
                        src="/assets/images/Dashboard.png"
                        alt="Dashboard Preview"
                        fill
                        className="object-contain p-2 group-hover:scale-105 transition-transform duration-700"
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/30 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors duration-300">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                            <Icon name="MagnifyingGlassPlusIcon" size={32} className="text-primary" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-primary/5 text-primary rounded-full text-sm font-semibold border border-primary/20 shadow-sm">
                      <Icon name="ShieldExclamationIcon" size={14} />
                      Risk Score
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-primary/5 text-primary rounded-full text-sm font-semibold border border-primary/20 shadow-sm">
                      <Icon name="CurrencyDollarIcon" size={14} />
                      Compensation
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-primary/5 text-primary rounded-full text-sm font-semibold border border-primary/20 shadow-sm">
                      <Icon name="SparklesIcon" size={14} />
                      Quick Insights
                    </span>
                  </div>
                </div>
              </div>

              {/* Checklist Preview */}
              <div className="group relative">
                <div className="bg-gradient-to-br from-surface to-surface/80 rounded-2xl p-6 border-2 border-border hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:scale-[1.02]">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-success/20 to-success/10 rounded-xl flex items-center justify-center shadow-lg border border-success/20 group-hover:scale-110 transition-transform duration-300">
                      <Icon name="ClipboardDocumentCheckIcon" size={28} className="text-success" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-text-primary mb-1">
                        Interactive Checklist
                      </h3>
                      <p className="text-sm font-medium text-text-secondary/80">Detailed item-by-item analysis</p>
                    </div>
                  </div>
                  <div 
                    className="relative rounded-xl overflow-hidden border border-border shadow-lg group-hover:shadow-2xl transition-all duration-500 bg-muted/30 cursor-pointer"
                    onClick={() => setSelectedImage('/assets/images/Checklist.png')}
                  >
                    <div className="aspect-video relative overflow-hidden flex items-center justify-center">
                      <Image
                        src="/assets/images/Checklist.png"
                        alt="Checklist Preview"
                        fill
                        className="object-contain p-2 group-hover:scale-105 transition-transform duration-700"
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/30 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors duration-300">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                            <Icon name="MagnifyingGlassPlusIcon" size={32} className="text-primary" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-error/10 to-error/5 text-error rounded-full text-sm font-semibold border border-error/20 shadow-sm">
                      <Icon name="ExclamationTriangleIcon" size={14} />
                      Red Flags
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-warning/10 to-warning/5 text-warning rounded-full text-sm font-semibold border border-warning/20 shadow-sm">
                      <Icon name="ClockIcon" size={14} />
                      Amber Items
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-success/10 to-success/5 text-success rounded-full text-sm font-semibold border border-success/20 shadow-sm">
                      <Icon name="CheckCircleIcon" size={14} />
                      Green Checks
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Guidance Previews - Carousel Style */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="group relative">
                <div className="bg-gradient-to-br from-surface to-surface/80 rounded-xl p-5 border-2 border-border hover:border-warning/50 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
                  <div 
                    className="relative rounded-lg overflow-hidden border border-border shadow-lg group-hover:shadow-xl transition-all duration-300 bg-muted/30 cursor-pointer"
                    onClick={() => setSelectedImage('/assets/images/guidance_1.png')}
                  >
                    <div className="aspect-[4/3] relative overflow-hidden flex items-center justify-center">
                      <Image
                        src="/assets/images/guidance_1.png"
                        alt="Negotiation Guidance Preview 1"
                        fill
                        className="object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 768px) 100vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors duration-300">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                            <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-warning" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-warning/10 via-warning/5 to-transparent rounded-lg border border-warning/20 group-hover:border-warning/40 transition-all duration-300">
                    <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center group-hover:bg-warning/30 transition-colors">
                      <Icon name="LightBulbIcon" size={18} className="text-warning" />
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      Negotiation Strategies
                    </span>
                  </div>
                </div>
              </div>

              <div className="group relative">
                <div className="bg-gradient-to-br from-surface to-surface/80 rounded-xl p-5 border-2 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
                  <div 
                    className="relative rounded-lg overflow-hidden border border-border shadow-lg group-hover:shadow-xl transition-all duration-300 bg-muted/30 cursor-pointer"
                    onClick={() => setSelectedImage('/assets/images/guidance_2.png')}
                  >
                    <div className="aspect-[4/3] relative overflow-hidden flex items-center justify-center">
                      <Image
                        src="/assets/images/guidance_2.png"
                        alt="Negotiation Guidance Preview 2"
                        fill
                        className="object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 768px) 100vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors duration-300">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                            <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-primary" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-lg border border-primary/20 group-hover:border-primary/40 transition-all duration-300">
                    <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                      <Icon name="ShieldExclamationIcon" size={18} className="text-primary" />
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      Legal Considerations
                    </span>
                  </div>
                </div>
              </div>

              <div className="group relative">
                <div className="bg-gradient-to-br from-surface to-surface/80 rounded-xl p-5 border-2 border-border hover:border-success/50 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
                  <div 
                    className="relative rounded-lg overflow-hidden border border-border shadow-lg group-hover:shadow-xl transition-all duration-300 bg-muted/30 cursor-pointer"
                    onClick={() => setSelectedImage('/assets/images/guidance_3.png')}
                  >
                    <div className="aspect-[4/3] relative overflow-hidden flex items-center justify-center">
                      <Image
                        src="/assets/images/guidance_3.png"
                        alt="Negotiation Guidance Preview 3"
                        fill
                        className="object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 768px) 100vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors duration-300">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                            <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-success" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-success/10 via-success/5 to-transparent rounded-lg border border-success/20 group-hover:border-success/40 transition-all duration-300">
                    <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center group-hover:bg-success/30 transition-colors">
                      <Icon name="ChatBubbleLeftRightIcon" size={18} className="text-success" />
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      Email Templates & Talking Points
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-surface/50 rounded-xl border border-border hover:border-primary/30 transition-all duration-300">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="ShieldCheckIcon" size={32} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">Risk Detection</h4>
                <p className="text-sm text-text-secondary">Identify red flags automatically</p>
              </div>

              <div className="text-center p-6 bg-surface/50 rounded-xl border border-border hover:border-primary/30 transition-all duration-300">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CurrencyDollarIcon" size={32} className="text-success" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">Compensation Breakdown</h4>
                <p className="text-sm text-text-secondary">Complete package analysis</p>
              </div>

              <div className="text-center p-6 bg-surface/50 rounded-xl border border-border hover:border-primary/30 transition-all duration-300">
                <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="LightBulbIcon" size={32} className="text-warning" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">Smart Guidance</h4>
                <p className="text-sm text-text-secondary">AI-powered recommendations</p>
              </div>

              <div className="text-center p-6 bg-surface/50 rounded-xl border border-border hover:border-primary/30 transition-all duration-300">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="ArrowDownTrayIcon" size={32} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">Export Reports</h4>
                <p className="text-sm text-text-secondary">PDF & JSON formats</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-16 max-w-2xl">
              <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                Everything You Need to Know
              </h2>
              <p className="text-xl text-text-secondary">
                Comprehensive analysis tools to help you make informed career decisions
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Feature 1 */}
              <div className="bg-gradient-to-br from-surface to-surface/50 rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="ShieldCheckIcon" size={28} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary mb-3">Risk Assessment & Red Flags</h3>
                    <p className="text-text-secondary mb-4 leading-relaxed">
                      Identify potential red flags and risky clauses in your offer letter. Our AI scans for:
                    </p>
                    <ul className="space-y-2 text-text-secondary">
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Non-compete and restrictive clauses</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>At-will employment terms</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Equity vesting schedules and conditions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Termination and severance terms</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="bg-gradient-to-br from-surface to-surface/50 rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-success/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="CurrencyDollarIcon" size={28} className="text-success" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary mb-3">Complete Compensation Analysis</h3>
                    <p className="text-text-secondary mb-4 leading-relaxed">
                      Get a detailed breakdown of your total compensation package:
                    </p>
                    <ul className="space-y-2 text-text-secondary">
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Base salary and bonus structures</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Equity and stock option valuation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Benefits package analysis (health, dental, 401k)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Total compensation comparison</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="bg-gradient-to-br from-surface to-surface/50 rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-warning/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="LightBulbIcon" size={28} className="text-warning" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary mb-3">AI-Powered Negotiation Guidance</h3>
                    <p className="text-text-secondary mb-4 leading-relaxed">
                      Receive personalized negotiation strategies and talking points:
                    </p>
                    <ul className="space-y-2 text-text-secondary">
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Industry benchmark comparisons</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Email templates for negotiations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Scripts for salary discussions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Priority negotiation points</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="bg-gradient-to-br from-surface to-surface/50 rounded-2xl p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="ClipboardDocumentCheckIcon" size={28} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary mb-3">Interactive Checklist</h3>
                    <p className="text-text-secondary mb-4 leading-relaxed">
                      Use our comprehensive interactive checklist to review:
                    </p>
                    <ul className="space-y-2 text-text-secondary">
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>All offer letter components</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Legal and compliance items</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Benefits and perks verification</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <span>Customizable priority items</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-b from-background to-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                Why Choose Offer XRay?
              </h2>
              <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                The most comprehensive AI-powered offer letter analysis platform
              </p>
            </div>

            <div className="max-w-4xl mx-auto divide-y divide-border border-t border-b border-border">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8 py-6">
                <div className="flex items-center gap-3 md:w-64 flex-shrink-0">
                  <Icon name="BoltIcon" size={24} className="text-primary flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-text-primary">Lightning Fast</h3>
                </div>
                <p className="text-text-secondary">Get complete analysis in minutes, not hours</p>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8 py-6">
                <div className="flex items-center gap-3 md:w-64 flex-shrink-0">
                  <Icon name="LockClosedIcon" size={24} className="text-success flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-text-primary">Secure by Design</h3>
                </div>
                <p className="text-text-secondary">Your documents are encrypted and never stored permanently</p>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8 py-6">
                <div className="flex items-center gap-3 md:w-64 flex-shrink-0">
                  <Icon name="AcademicCapIcon" size={24} className="text-warning flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-text-primary">AI-Powered</h3>
                </div>
                <p className="text-text-secondary">Advanced AI trained on real offer letters and employment contracts</p>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8 py-6">
                <div className="flex items-center gap-3 md:w-64 flex-shrink-0">
                  <Icon name="DocumentTextIcon" size={24} className="text-primary flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-text-primary">Export Reports</h3>
                </div>
                <p className="text-text-secondary">Download comprehensive reports in multiple formats</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Ready to Analyze Your Offer?
            </h2>
            <p className="text-xl text-text-secondary mb-10 max-w-2xl mx-auto">
              Get a clear, objective read on your offer before you sign, so you can negotiate from a position of knowledge, not guesswork
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/signup"
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-all duration-300 shadow-2xl hover:shadow-primary/50 hover:scale-105"
              >
                <Icon name="UserPlusIcon" size={24} />
                <span>Start Free Analysis</span>
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-surface text-text-primary border-2 border-border rounded-xl font-semibold text-lg hover:bg-muted hover:border-primary/50 transition-all duration-300"
              >
                <Icon name="InformationCircleIcon" size={24} />
                <span>View Pricing</span>
              </Link>
            </div>
            <p className="mt-8 text-text-secondary">
              New to this? Read{' '}
              <Link href="/blog/offer-letter-analyzer" className="text-primary underline hover:no-underline">
                how an offer letter analyzer works
              </Link>{' '}
              before you upload your first document.
            </p>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm border border-white/20"
              aria-label="Close modal"
            >
              <Icon name="XMarkIcon" size={24} className="text-white" />
            </button>
            <div 
              className="relative w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <Image
                src={selectedImage}
                alt="Full size preview"
                fill
                className="object-contain p-8"
                sizes="100vw"
                priority
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

